import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../components/header";
import StoryMenu from "../../components/story/menu";
import StoryItem from "../../components/story/item";
import contentHooks from "../../hooks/contentHooks";
import { useNavigate } from "react-router-dom";

const StoryRiskyPregnancy = () => {
  const navigate = useNavigate();
  const [riskyData, setRiskyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastTimestamp, setLastTimestamp] = useState(0);

  const observerRef = useRef();
  const lastItemRef = useRef();

  const handleItemClick = (id) => {
    navigate(`/story/question/${id}`);
  };

  const loadRiskyPregnancies = useCallback(
    async (timestamp) => {
      if (isLoading) return;
      setIsLoading(true);
      setError(null);

      try {
        const result = await contentHooks.contentList({
          last_time_stamp: timestamp,
          type: "고위험 임신",
        });

        if (result.IsError) {
          setError("데이터를 불러오는데 실패했습니다.");
          return;
        }

        const newRiskyPregnancies = result.data.hits;

        if (newRiskyPregnancies.length === 0) {
          setHasMore(false);
        } else {
          setRiskyData((prev) =>
            timestamp === 0
              ? newRiskyPregnancies
              : [...prev, ...newRiskyPregnancies]
          );

          const newLastTimestamp =
            newRiskyPregnancies[newRiskyPregnancies.length - 1].created_at;
          setLastTimestamp(newLastTimestamp);
        }
      } catch (err) {
        console.error("데이터 로드 에러:", err);
        setError("네트워크 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading]
  );

  useEffect(() => {
    loadRiskyPregnancies(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentLastItemRef = lastItemRef.current;

    const handleObserver = (entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoading && lastTimestamp > 0) {
        loadRiskyPregnancies(lastTimestamp);
      }
    };

    const option = {
      root: null,
      rootMargin: "20px",
      threshold: 0.5,
    };

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(handleObserver, option);

    if (currentLastItemRef && hasMore && !isLoading) {
      observerRef.current.observe(currentLastItemRef);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isLoading, hasMore, lastTimestamp, loadRiskyPregnancies]);

  const handleRefresh = () => {
    setRiskyData([]);
    setLastTimestamp(0);
    setHasMore(true);
    setError(null);
    loadRiskyPregnancies(0);
  };

  if (error) {
    return (
      <div
        className="flex flex-col pt-[3.125rem] md:pt-[4.375rem] bg-white"
        style={{ 
          minHeight: "calc(var(--vh, 1vh) * 100)",
          paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
        }}
      >
        <Header selectedMenu={1} />
        <div className="pb-[1.875rem]">
          <StoryMenu subMenuType={1} />
          <div className="text-center py-[1rem] text-red-500">
            <p>{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-[0.5rem] px-[1rem] py-[0.5rem] bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col pt-[3.125rem] md:pt-[4.375rem] bg-white"
      style={{ 
        minHeight: "calc(var(--vh, 1vh) * 100)",
        paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
      }}
    >
      <Header selectedMenu={1} />
      <div className="pb-[1.875rem]">
        <StoryMenu subMenuType={2} />
        <div className="text-center w-full">
          {riskyData.length > 0 ? (
            riskyData.map((item, index) => {
              const isLastItem = index === riskyData.length - 1;

              return (
                <div
                  key={`${item.id}-${index}`}
                  ref={isLastItem ? lastItemRef : null}
                >
                  <StoryItem
                    image={item.content_thumbnail}
                    title={item.content_title}
                    no={item.id}
                    onClick={() => handleItemClick(item.id)}
                  />
                </div>
              );
            })
          ) : !isLoading && !error ? (
            <div className="flex flex-col items-center justify-center py-[4rem]">
              <p className="text-gray-500 text-lg mb-[0.5rem]">
                아직 등록된 게시글이 없습니다.
              </p>
              <p className="text-gray-400 text-sm">
                곧 새로운 내용이 업데이트될 예정입니다.
              </p>
            </div>
          ) : null}
        </div>

        {isLoading && (
          <div className="flex justify-center py-[2rem]">
            <div className="animate-spin rounded-full h-[2rem] w-[2rem] border-b-2 border-gray-900"></div>
          </div>
        )}

        {!hasMore && riskyData.length > 0 && (
          <div className="text-center py-[2rem] text-gray-500">
            모든 게시글을 불러왔습니다.
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryRiskyPregnancy;
